<template>
  <div :class="`dn-gallery ${layout}`">
    <template v-if="isScrolly">
      <div
        v-for="(image, index) in items"
        :key="index"
        class="dn-gallery-scrolly-item"
        :style="{ '--i': index }"
      >
        <DnArticleImage
          :src="image.srcFastly"
          :caption="image.caption"
          :alt="image.alt"
          :credit="image.credit"
        />
      </div>
    </template>

    <template v-else>
      <AtlasSwiper
        v-bind="swiperArgs"
        :keyboard="false"
        tabindex="0"
        @keydown="handleGalleryKeydown"
      >
        <SwiperSlide
          v-for="(image, index) in items"
          :key="index"
        >
          <DnArticleImage
            :src="image.srcFastly"
            :caption="image.caption"
            :alt="image.alt"
            :credit="image.credit"
          />
        </SwiperSlide>
        <div class="atlas-swiper-navigation">
          <div class="carousel-prev">
            <AtlasIcon name="common/arrow-left" />
          </div>
          <div class="pagination" />
          <div class="carousel-next">
            <AtlasIcon name="common/arrow-right" />
          </div>
        </div>
      </AtlasSwiper>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue';
import { AtlasSwiper, AtlasIcon, AtlasImage } from '@nhst/ui';
import { SwiperSlide } from 'swiper/vue';
import DnArticleImage from '~/components/DnArticleImage/DnArticleImage.vue';
import 'swiper/css/pagination';

const props = defineProps({
  items: {
    type: Array as PropType<Record<string, unknown>[]>,
    required: false,
    default: () => []
  },
  layout: {
    type: String,
    default: 'default'
  }
});

// Using fraction pagination if there are more than 8 images
const useFractionPagination = computed(() => props.items.length > 8);

const swiperArgs = computed(() => ({
  class: 'article-image-carousel three-two',
  loop: false,
  slidesPerView: 1,
  navigation: {
    enabled: true,
    addIcons: false,
    nextEl: '.carousel-next',
    prevEl: '.carousel-prev'
  },
  pagination: {
    clickable: !useFractionPagination.value,
    el: '.pagination',
    type: useFractionPagination.value ? 'fraction' : 'bullets'
  }
} as Record<string, unknown>));

const isScrolly = computed(() => props.layout === 'scrolly');

function handleGalleryKeydown (event: KeyboardEvent) {
  const root = event.currentTarget as HTMLElement | null;
  if (!root) {
    return;
  }

  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    root.querySelector<HTMLElement>('.carousel-prev')?.click();
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    root.querySelector<HTMLElement>('.carousel-next')?.click();
  }
}
</script>

<style lang="scss" scoped>
.dn-gallery {
  --atlas-article-top_padding: 0;

  .pagination{
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .swiper-pagination-fraction{
    font-family: var(--typography-utility-m-font-family);
    font-size: var(--typography-utility-l-font-size);
    font-weight: 400;
  }

  .atlas-image {
    width: inherit;

    &.default{
      inline-size: inherit;
    }
  }

  &.scrolly {
    position: relative;
    display: block;
    width: 100%;
    margin-inline: 0;

    /* total scroll length per image: 100dvh image + (this - 100dvh) of dwell */
    --dn-gallery-stage-scroll: 100svh;

    .dn-gallery-scrolly-item {
      position: sticky;
      top: 0;
      width: 100vw;
      margin-inline: calc(50% - 50vw);
      block-size: var(--dn-gallery-stage-scroll);
      display: grid;
      grid-template-rows: 100svh;
      align-content: start;
      overflow: hidden;
      z-index: var(--i, 0);
    }

    :deep(.atlas-image) {
      inline-size: 100%;
      block-size: 100svh;
      max-width: none;
      margin: 0;

      --atlas-image_grid-template: 1fr / 1fr;
      --atlas-image_picture_grid-area: 1 / 1 / -1 / -1;
      --atlas-image_picture_aspect-ratio: auto;
      --atlas-image_picture_width: 100%;
      --atlas-image_picture_max-width: 100%;
      --atlas-image_picture_height: 100%;
      --atlas-image_picture_max-height: 100%;
      --atlas-image_picture_border-radius: 0;
      --atlas-image_picture_background: transparent;
      --atlas-image_figcaption_grid-area: 1 / 1 / -1 / -1;
      --atlas-image_figcaption_place-self: end center;
      --atlas-image_figcaption_color: #fff;
      --atlas-image_figcaption_padding:  var(--site-padding) calc(var(--atlas_gap) * 2);
      --atlas-image_figcaption_background: var(--color-brand-utility-800);
      --atlas-image_figcaption_max-width: 680px;
      --atlas-image_figcaption_font-family: var(--typography-editorial-titlepiece-min-font-family);
      --atlas-image_figcaption_font-size: 22px;
      --atlas-image_figcaption_font-weight: 400;
      --atlas-image_figcaption_line-height: 130%;
      --atlas-image_figcaption_letter-spacing: 0;
      --atlas-image_figcaption_text-align: center;
    }

    :deep(.atlas-image picture) {
      inline-size: 100%;
      block-size: 100%;
      aspect-ratio: auto;
    }

    :deep(.atlas-image img) {
      inline-size: 100%;
      block-size: 100%;
      aspect-ratio: auto;
      object-fit: cover;
      object-position: center center;
    }

    :deep(.atlas-image figcaption) {
      place-self: center center;
      }
  }
}
</style>
